
<template>
  <v-card v-if="activity">
    <div class="stopwatch-container">
      <span class="headline primary--text font-weight-medium">
        <v-icon class="primary--text" left>mdi-clock</v-icon>
        <span class="stopwatch-title">{{
          currentActivityElapsedTime ? "Current Activity" : "Latest Activity"
        }}</span>
      </span>
      <div class="value-container condensed">
        <header v-text="`${activity.activityName} - ${activity.code}`" />
        <span class="activity-value" v-text="activity.locationName" />
      </div>
      <div class="value-container full">
        <header class="value-header" v-text="'Activity'" />
        <span
          class="activity-value"
          v-text="`${activity.activityName} - ${activity.code}`"
        />
      </div>
      <div class="value-container full">
        <header class="value-header" v-text="'Location'" />
        <span class="activity-value" v-text="activity.locationName" />
      </div>
      <div class="value-container">
        <header class="value-header" v-text="'Start (LT)'" />
        <span class="activity-value">
          {{ activity.startDateTimeLt | removeTAndSecondsAndFormatDate }}
        </span>
      </div>
      <div class="value-container">
        <header
          class="value-header"
          v-text="currentActivityElapsedTime ? 'Elapsed' : 'End (LT)'"
        ></header>
        <span
          :class="
            currentActivityElapsedTime
              ? 'activity-value font-weight-bold'
              : 'activity-value'
          "
        >
          {{
            currentActivityElapsedTime
              ? currentActivityElapsedTime
              : $options.filters.removeTAndSecondsAndFormatDate(
                  activity.endDateTimeLt
                )
          }}
        </span>
      </div>
      <div class="actions-container">
        <v-btn
          v-if="currentActivityElapsedTime"
          color="#e20505"
          small
          fab
          depressed
          dark
          @click="stopCurrentActivity(activity)"
        >
          <v-icon>mdi-stop</v-icon>
        </v-btn>
        <v-btn
          v-else
          color="#49c800"
          small
          fab
          depressed
          dark
          @click="onResumeClick()"
        >
          <v-icon>mdi-play</v-icon>
        </v-btn>
        <v-icon
          :disabled="
            currentActivityElapsedTime !== null || !isClientConnectedToServer
          "
          class="mx-2"
          @click="onEditClick(activity)"
          >mdi-pencil</v-icon
        >
        <v-icon
          @click="onDeleteClick(activity)"
          :disabled="!isClientConnectedToServer && !activity.idc"
          >mdi-delete</v-icon
        >
      </div>
    </div>
  </v-card>
</template>

<script>
import { mapState, mapGetters } from 'vuex';
import ResponsiveSizeHelperMixin from '@/mixins/ResponsiveSizeHelperMixin';
import VesselActivityHelperMixin from '@/mixins/VesselActivityHelperMixin';
import dateTimeHelpers from '../../mixins/DateTimeHelpers';

export default {
  name: 'ActivityStopwatch',
  props: {
    onEditClick: Function,
    onDeleteClick: Function,
  },
  mixins: [ResponsiveSizeHelperMixin, VesselActivityHelperMixin],
  computed: {
    ...mapState(['voyageReportsStore']),
    ...mapGetters({
      getSyncedAndUnSyncedVesselActivities: 'voyageReportsStore/getSyncedAndUnSyncedVesselActivities',
      isClientConnectedToServer: 'userStore/isClientConnectedToServer',
    }),
    vesselActivities() {
      return this.getSyncedAndUnSyncedVesselActivities.filter((x) => x.code !== '03');
    },
  },
  watch: {
    vesselActivities(val) {
      this.activity = null;
      if (this.stopwatchInterval) this.clearStopwatchInterval();

      const [currentActivity] = val;
      if (currentActivity) {
        this.activity = currentActivity;
        if (!this.activity.endDateTimeLt) {
          this.resumeActivity();
        }
      }
    },
  },
  data() {
    return {
      activity: null,
      currentActivityElapsedTime: null,
      stopwatchInterval: null,
    };
  },
  methods: {
    runStopwatch() {
      // elapsed time calculated from the difference between the current time
      // and start time of the activity
      const newStart = Date.parse(this.activity.startDateTimeLt);
      const newCurrent = new Date();
      const elapsedMs = newCurrent - newStart;

      if (elapsedMs < 0) {
        this.currentActivityElapsedTime = '--:--:--';
      } else {
        this.currentActivityElapsedTime = this.msToTimerString(elapsedMs);
      }
    },
    onResumeClick() {
      // When activity is resumed - clear the End DateTime and update
      // The Watcher for vesselActivities will trigger resumeActivity()
      this.activity.endDateTimeLt = null;
      const updatedActivity = { ...this.activity };
      updatedActivity.activityId = this.activity.activity ? this.activity.activity.id
        : this.activity.activityId;

      this.$store
        .dispatch('voyageReportsStore/updateVesselActivity', updatedActivity)
        .then((resp) => {
          this.onSuccess('Current Activity Resumed', resp);
        })
        .catch((err) => {
          this.onError('Failed to update Vessel Activity');
          console.error(err);
        });
    },
    resumeActivity() {
      this.activity.endDateTimeLt = null;
      if (!this.stopwatchInterval) {
        this.stopwatchInterval = setInterval(() => this.runStopwatch(), 10);
      }

      // event emitted to parent to disable 'Add' button while activity is in-progress
      this.$emit('activityResumed');
    },
    stopCurrentActivity(activity) {
      const endDate = dateTimeHelpers.getCurrentDateLt();
      const endTime = dateTimeHelpers.getCurrentTimeLt();
      const endDateTimeLt = `${endDate}T${endTime}:00`;

      // set new end date to activity update model 'endDateTimeLt'
      const updatedActivity = { ...activity };
      updatedActivity.endDateTimeLt = endDateTimeLt;
      updatedActivity.activityId = activity.activity ? activity.activity.id : activity.activityId;

      // Update
      this.$store
        .dispatch('voyageReportsStore/updateVesselActivity', updatedActivity)
        .then((resp) => {
          this.onSuccess('Vessel Activity Updated', resp);
          // once stopped and updated - clear timer interval and elapsed time
          if (this.stopwatchInterval) this.clearStopwatchInterval();
        })
        .then(() => {
          this.$nextTick(() => {
            const isDischargeActivity = this.isDischargeActivity(updatedActivity.code);
            if (isDischargeActivity
            && updatedActivity.endDateTimeLt !== null
            && this.isClientConnectedToServer) {
              this.emitEndVoyageDischargeEvent();
            }
          });
        })
        .catch((err) => {
          this.onError('Failed to update Vessel Activity');
          console.error(err);
        });
    },
    clearStopwatchInterval() {
      clearInterval(this.stopwatchInterval);
      this.stopwatchInterval = null;
      this.currentActivityElapsedTime = null;
    },
    msToTimerString(durationMs) {
      let seconds = Math.floor((durationMs / 1000) % 60);
      let minutes = Math.floor((durationMs / (1000 * 60)) % 60);
      let hours = Math.floor((durationMs / (1000 * 60 * 60)));

      hours = (hours < 10) ? `0${hours}` : hours;
      minutes = (minutes < 10) ? `0${minutes}` : minutes;
      seconds = (seconds < 10) ? `0${seconds}` : seconds;

      return `${hours}:${minutes}:${seconds}`;
    },
    onSuccess(message, data) {
      this.$toast.success(message);
    },
    onError(message, data) {
      this.$toast.error(message);
    },
  },
  // Clear interval before component is destroyed
  beforeDestroy() {
    if (this.stopwatchInterval) this.clearStopwatchInterval();
  },
};
</script>

<style scoped>
.stopwatch-container {
  display: block;
  align-items: center;
  text-align: start;
  padding: 16px;
}
.value-container {
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  margin-top: 8px;
  min-width: 200px;
}
.condensed {
  display: none;
}
.full {
  display: flex;
}
.value-header {
  display: block;
  font-weight: bold;
  font-size: 14px;
}
.activity-value {
  font-weight: 500;
  font-size: 14px;
}
.actions-container {
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: flex-end;
  margin-top: 16px;
}

@media only screen and (min-width: 960px) {
  .stopwatch-container {
    display: flex;
    flex-direction: row;
    justify-content: space-between;
  }
  .stopwatch-title {
    display: none;
  }
  .value-container {
    display: block;
  }
  .condensed {
    display: block;
  }
  .full {
    display: none;
  }
  .actions-container {
    margin: 0;
  }
  .value-header {
    font-size: 14px;
    font-weight: 700;
    color: #00000099;
  }
  .activity-value {
    font-weight: 500;
    font-size: 18px;
  }
  .condensed header {
    font-size: 22px;
  }
  .condensed span {
    font-size: 18px;
  }
}

@media only screen and (min-width: 1264px) {
  .stopwatch-title {
    display: initial;
  }
  .headline {
    min-width: 200px !important;
  }
  .condensed {
    display: none;
  }
  .full {
    display: block;
  }
  .activity-value {
    font-size: 24px;
    font-weight: bold;
    text-transform: uppercase;
  }
}
</style>
